import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { SERVICE_MATRIX_IDS, type ServiceDef, type PlanTier } from '../data/services';
import { loadPlanAccessMatrix, type AccessMatrix } from '../lib/planAccessMatrix';

// ─────────────────────────────────────────
// Access control surface
//
// 1.  hasTierAccess(profile, tier)
//     Pure, hook-free. Sidebar uses this for fast static routing
//     hints, it does NOT consult the admin matrix (would require an
//     async load on every render). Admin, unlimited_access and
//     tier >= required all resolve to true.
//
// 2.  usePlanAccess(service)
//     Matrix-aware. Used by <ServiceGate />. Loads the
//     plan_access_matrix once (cached in memory for ~5 min) and
//     allows access if ANY matrix entry for the catalog slug is
//     enabled for the user's tier. Falls back to service.requiredTier
//     when the slug is not mapped or the matrix is unavailable.
// ─────────────────────────────────────────

export type AccessState = 'loading' | 'allowed' | 'denied';

export const TIER_ORDER: Record<PlanTier, number> = {
  free:    0,
  monthly: 1,
  annual:  2,
};

interface MinimalProfile {
  subscription_tier?: string | null;
  unlimited_access?:  boolean | null;
  role?:              string | null;
}

export function hasTierAccess(
  profile: MinimalProfile | null | undefined,
  requiredTier: PlanTier,
): boolean {
  if (!profile) return false;
  if (profile.role === 'admin' || profile.unlimited_access) return true;
  const userTier = (profile.subscription_tier as PlanTier | undefined) ?? 'free';
  return (TIER_ORDER[userTier] ?? 0) >= (TIER_ORDER[requiredTier] ?? 0);
}

export function usePlanAccess(service: ServiceDef | null | undefined): AccessState {
  const { profile, loading } = useAuth();
  const [matrix, setMatrix] = useState<AccessMatrix | null>(null);
  const [matrixLoaded, setMatrixLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    loadPlanAccessMatrix().then(m => {
      if (!active) return;
      setMatrix(m);
      setMatrixLoaded(true);
    });
    return () => { active = false; };
  }, []);

  if (loading) return 'loading';
  if (!service) return 'allowed';        // non-catalog route → pass
  if (!matrixLoaded) return 'loading';   // wait before deciding
  if (!profile) return 'denied';
  if (profile.role === 'admin' || profile.unlimited_access) return 'allowed';

  const userTier = (profile.subscription_tier as PlanTier | undefined) ?? 'free';
  const matrixIds = SERVICE_MATRIX_IDS[service.slug];

  // Matrix-driven check: allowed if ANY matrix id for this catalog
  // slug is enabled for the user's tier.
  if (matrix && matrixIds && matrixIds.length > 0) {
    const allowed = matrixIds.some(id => matrix[id]?.[userTier]?.enabled === true);
    return allowed ? 'allowed' : 'denied';
  }

  // Fallback to the static requiredTier on the catalog entry.
  return hasTierAccess(profile, service.requiredTier) ? 'allowed' : 'denied';
}
